import { LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { AppScreen, Card, IconAction } from '@/components/ui/scene';
import { Switch } from '@/components/ui/switch';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { UnavailableState } from '@/components/ui/UnavailableState';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import type {
  EventAgeRestriction,
  PatchVenueRequest,
  PresignedUpload,
  SignUploadRequest,
  VenueCategory,
  VenueDetail,
  VenueHour,
  VenueSocialLink,
  VenueSocialPlatform,
  VenueWeekday,
} from '@/lib/api/apiTypes';
import {
  UploadPurposeVenueImage,
  VenueCategoryOther,
  VenueSocialPlatformFacebook,
  VenueSocialPlatformInstagram,
  VenueSocialPlatformLinktree,
  VenueSocialPlatformSpotify,
  VenueSocialPlatformTiktok,
  VenueSocialPlatformX,
  VenueSocialPlatformYoutube,
  VenueWeekdayFriday,
  VenueWeekdayMonday,
  VenueWeekdaySaturday,
  VenueWeekdaySunday,
  VenueWeekdayThursday,
  VenueWeekdayTuesday,
  VenueWeekdayWednesday,
} from '@/lib/api/apiTypes';
import { useAuth } from '@/lib/auth/auth';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import {
  EVENT_AGE_RESTRICTIONS,
  EVENT_TAG_GROUPS,
  getEventTagColors,
} from '@/features/events/eventTags';
import {
  addSentryActionBreadcrumb,
  captureSentryException,
  createSentryInputTracker,
} from '@/lib/analytics/sentry';
import { getRouteEntityID, getVenueHref, getVenuePath } from '@/lib/navigation/routes';
import { cn } from '@/lib/utils';
import { VENUE_OWNER_CATEGORIES, getVenueCategoryConfig } from '@/features/venues/venueCategories';
import { useUpdateVenue } from '@/features/venues/useUpdateVenue';
import { useVenueDetail } from '@/features/venues/useVenueDetail';
import DateTimePicker, {
  DateTimePickerAndroid,
  type DateTimePickerEvent,
} from '@react-native-community/datetimepicker';
import { useNavigation, usePreventRemove } from '@react-navigation/native';
import * as ImagePicker from 'expo-image-picker';
import { Redirect, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import {
  ArrowLeftIcon,
  AtSign,
  Camera,
  CheckIcon,
  Disc3,
  Facebook,
  Image,
  Instagram,
  Link,
  Music2,
  StoreIcon,
  TreePalm,
  Trash2,
  Youtube,
} from 'lucide-react-native';
import * as React from 'react';
import {
  Alert,
  Image as RNImage,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  View,
} from 'react-native';

const VENUE_WEEKDAYS: VenueWeekday[] = [
  VenueWeekdayMonday,
  VenueWeekdayTuesday,
  VenueWeekdayWednesday,
  VenueWeekdayThursday,
  VenueWeekdayFriday,
  VenueWeekdaySaturday,
  VenueWeekdaySunday,
];

type PickerField = 'opens_at' | 'closes_at';
type ActivePicker = {
  day: VenueWeekday;
  field: PickerField;
} | null;

export default function EditVenueScreen() {
  const params = useLocalSearchParams<{ id: string }>();
  const venueID = getRouteEntityID(params.id);
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const request = useAuth((state) => state.request);
  const { membershipsQuery } = useAccountContext();
  const venueDetailQuery = useVenueDetail(venueID);
  const updateVenue = useUpdateVenue(venueID);
  const { dispatch } = useNavigation();
  const { back, canGoBack, replace } = useRouter();
  const [draft, setDraft] = React.useState<PatchVenueRequest | null>(null);
  const [activePicker, setActivePicker] = React.useState<ActivePicker>(null);
  const [socialInput, setSocialInput] = React.useState('');
  const [isCategoryPickerVisible, setIsCategoryPickerVisible] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [uploadingField, setUploadingField] = React.useState<'logo_url' | 'cover_url' | null>(null);
  const [isLeaving, setIsLeaving] = React.useState(false);
  const detail = venueDetailQuery.data ?? null;
  const venuePath = React.useMemo(
    () =>
      venueID
        ? getVenuePath(venueID, detail ? { slug: detail.slug, name: detail.name } : undefined)
        : '/(tabs)/events',
    [detail, venueID]
  );
  const venueNameInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'name',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );
  const venueDescriptionInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'description',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );
  const venueAddressInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'address',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );
  const venuePhoneInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'phone',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );
  const venueWebsiteInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'website',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );
  const venueBookingUrlInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'booking_url',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );
  const socialInputTracker = React.useMemo(
    () =>
      createSentryInputTracker({
        screen: 'venue_edit',
        field: 'social_input',
        form: 'venue_profile',
        entityId: venueID,
      }),
    [venueID]
  );

  React.useEffect(() => {
    if (!detail || draft) {
      return;
    }

    setDraft(buildVenueDraft(detail));
  }, [detail, draft]);

  const normalizedInitial = React.useMemo(
    () => (detail ? normalizeVenueDraft(buildVenueDraft(detail)) : null),
    [detail]
  );
  const normalizedDraftResult = React.useMemo(() => {
    if (!draft) {
      return { value: null, error: null as string | null };
    }

    try {
      return { value: normalizeVenueDraft(draft), error: null as string | null };
    } catch (draftError) {
      return {
        value: null,
        error: draftError instanceof Error ? draftError.message : 'Unable to validate this venue.',
      };
    }
  }, [draft]);
  const detectedSocialPlatform = React.useMemo(() => detectSocialPlatformPreview(socialInput), [socialInput]);
  const orgMembershipWithVenueAccess = React.useMemo(
    () =>
      membershipsQuery.data?.memberships.find(
        (membership) =>
          (membership.role === 'owner' ||
            membership.role === 'manager' ||
            membership.role === 'social') &&
          membership.venues.some((venue) => venue.venue_id === venueID)
      ) ?? null,
    [membershipsQuery.data?.memberships, venueID]
  );
  const canManageVenue = Boolean(detail?.viewer_is_owner || orgMembershipWithVenueAccess);
  const isDirty =
    Boolean(normalizedInitial && normalizedDraftResult.value) &&
    JSON.stringify(normalizedInitial) !== JSON.stringify(normalizedDraftResult.value);
  const saveError = error ?? normalizedDraftResult.error;

  usePreventRemove(Boolean(isDirty && !isLeaving && !updateVenue.isPending), ({ data }) => {
    confirmDiscardChanges(() => {
      setIsLeaving(true);
      dispatch(data.action);
    });
  });

  React.useEffect(() => {
    if (!isDirty || isLeaving || Platform.OS !== 'web') {
      return;
    }

    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty, isLeaving]);

  const leaveScreen = React.useCallback(() => {
    setIsLeaving(true);
    if (canGoBack()) {
      back();
      return;
    }

    replace(venuePath as never);
  }, [back, canGoBack, replace, venuePath]);

  const handleBack = React.useCallback(() => {
    if (!isDirty) {
      leaveScreen();
      return;
    }

    confirmDiscardChanges(leaveScreen);
  }, [isDirty, leaveScreen]);

  const handleFieldChange = React.useCallback(
    <K extends keyof PatchVenueRequest>(field: K, value: PatchVenueRequest[K]) => {
      setDraft((current) => (current ? { ...current, [field]: value } : current));
      setError(null);
    },
    []
  );

  const handleToggleTag = React.useCallback((value: string) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }

      const nextTags = current.tags.includes(value)
        ? current.tags.filter((tag) => tag !== value)
        : [...current.tags, value];
      return { ...current, tags: nextTags };
    });
    setError(null);
  }, []);

  const handleHourChange = React.useCallback(
    (day: VenueWeekday, update: Partial<VenueHour>) => {
      setDraft((current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          hours: current.hours.map((hour) =>
            hour.day === day
              ? {
                  ...hour,
                  ...update,
                }
              : hour
          ),
        };
      });
      setError(null);
    },
    []
  );

  const handleToggleClosed = React.useCallback(
    (day: VenueWeekday, closed: boolean) => {
      handleHourChange(day, {
        closed,
        opens_at: closed ? null : getExistingHour(draft?.hours, day)?.opens_at ?? '17:00',
        closes_at: closed ? null : getExistingHour(draft?.hours, day)?.closes_at ?? '23:00',
      });
    },
    [draft?.hours, handleHourChange]
  );

  const openPicker = React.useCallback(
    (day: VenueWeekday, field: PickerField) => {
      const hour = getExistingHour(draft?.hours, day);
      if (!hour || hour.closed) {
        return;
      }

      const currentValue = hour[field] ?? (field === 'opens_at' ? '17:00' : '23:00');
      if (Platform.OS === 'android') {
        DateTimePickerAndroid.open({
          value: timeStringToDate(currentValue),
          mode: 'time',
          is24Hour: true,
          onChange: (event: DateTimePickerEvent, selectedDate?: Date) => {
            if (event.type !== 'set' || !selectedDate) {
              return;
            }

            handleHourChange(day, { [field]: dateToTimeString(selectedDate) } as Partial<VenueHour>);
          },
        });
        return;
      }

      if (Platform.OS === 'ios') {
        setActivePicker((current) =>
          current?.day === day && current.field === field ? null : { day, field }
        );
      }
    },
    [draft?.hours, handleHourChange]
  );

  const handleAddSocial = React.useCallback(() => {
    if (!draft) {
      return;
    }

    try {
      const nextLink = normalizeSocialLinkInput(socialInput);
      const hasDuplicate = draft.social_links.some(
        (link) => link.platform === nextLink.platform && link.url === nextLink.url
      );
      if (hasDuplicate) {
        setError('That social link is already added.');
        return;
      }

      handleFieldChange('social_links', [...draft.social_links, nextLink]);
      setSocialInput('');
    } catch (socialError) {
      setError(socialError instanceof Error ? socialError.message : 'Enter a valid social link.');
    }
  }, [draft, handleFieldChange, socialInput]);

  const handleRemoveSocial = React.useCallback(
    (target: VenueSocialLink) => {
      if (!draft) {
        return;
      }

      handleFieldChange(
        'social_links',
        draft.social_links.filter((link) => !(link.platform === target.platform && link.url === target.url))
      );
    },
    [draft, handleFieldChange]
  );

  const handlePickImage = React.useCallback(
    async (field: 'logo_url' | 'cover_url') => {
      const permission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!permission.granted) {
        Alert.alert('Permission needed', 'Photo library access is required to upload venue images.');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ['images'],
        quality: 0.85,
        allowsEditing: true,
        aspect: field === 'logo_url' ? [1, 1] : [16, 9],
      });

      if (result.canceled || !result.assets.length) {
        return;
      }

      const asset = result.assets[0];
      setUploadingField(field);
      setError(null);
      try {
        const uploadedURL = await uploadVenueImage(request, asset, field);
        handleFieldChange(field, uploadedURL);
      } catch (uploadError) {
        captureSentryException(uploadError, {
          tags: { area: 'upload', asset: field === 'logo_url' ? 'venue_logo' : 'venue_cover' },
          extra: {
            fileName: asset.fileName ?? `${field}.jpg`,
            mimeType: asset.mimeType ?? 'image/jpeg',
          },
        });
        Alert.alert(
          'Upload failed',
          getAPIErrorRecoveryCopy(uploadError, 'venue', 'Unable to upload that image right now. Please try again.')
        );
      } finally {
        setUploadingField(null);
      }
    },
    [handleFieldChange, request]
  );

  const handleSave = React.useCallback(async () => {
    if (!normalizedDraftResult.value || !venueID) {
      return;
    }

    setError(null);
    try {
      addSentryActionBreadcrumb({
        screen: 'venue_edit',
        action: 'save_venue',
        data: {
          venue_id: venueID,
          social_count: normalizedDraftResult.value.social_links.length,
          tag_count: normalizedDraftResult.value.tags.length,
        },
      });
      setIsLeaving(true);
      const savedVenue = await updateVenue.mutateAsync(normalizedDraftResult.value);
      replace(
        getVenueHref(savedVenue.id, {}, { slug: savedVenue.slug, name: savedVenue.name }) as never
      );
    } catch (saveError) {
      setIsLeaving(false);
      setError(getAPIErrorRecoveryCopy(saveError, 'venue', 'Unable to save venue changes.'));
    }
  }, [normalizedDraftResult.value, replace, updateVenue, venueID]);

  if (isHydrating) {
    return (
      <AppScreen edges={['top', 'bottom']}>
        <LoadingState label="Loading venue editor..." />
      </AppScreen>
    );
  }

  if (!session) {
    return <Redirect href={{ pathname: '/sign-in', params: { next: venuePath } }} />;
  }

  if (venueDetailQuery.isPending && !detail) {
    return (
      <AppScreen edges={['top', 'bottom']}>
        <Stack.Screen options={{ headerShown: false }} />
        <LoadingState label="Loading venue editor..." />
      </AppScreen>
    );
  }

  if (!detail) {
    return (
      <AppScreen edges={['top', 'bottom']}>
        <Stack.Screen options={{ headerShown: false }} />
        <UnavailableState
          eyebrow="Venue Editor"
          icon={StoreIcon}
          title="We couldn't open this venue editor"
          body="That edit page is not available right now. You can jump back to the venue page if it is still live, or head back to the feed."
          detail={getAPIErrorRecoveryCopy(venueDetailQuery.error, 'venue', 'This venue could not be loaded right now.')}
          primaryActionLabel="Back to venue"
          onPrimaryAction={() => replace(venuePath as never)}
          secondaryActionLabel="Browse events"
          onSecondaryAction={() => replace('/(tabs)/events')}
        />
      </AppScreen>
    );
  }

  if (!canManageVenue) {
    return <Redirect href={venuePath as any} />;
  }

  if (!draft) {
    return (
      <AppScreen edges={['top', 'bottom']}>
        <LoadingState label="Preparing venue editor..." />
      </AppScreen>
    );
  }

  const categoryConfig = getVenueCategoryConfig(draft.category);
  const canSave =
    Boolean(isDirty && normalizedDraftResult.value) &&
    !normalizedDraftResult.error &&
    !updateVenue.isPending &&
    uploadingField === null;

  return (
    <AppScreen edges={['top', 'bottom']}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: !isDirty }} />
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        className="flex-1">
        <View className="flex-1">
          <ScrollView
            className="flex-1"
            contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 12, paddingBottom: 160, gap: 16 }}
            keyboardShouldPersistTaps="handled">
            <View className="flex-row items-center justify-between">
              <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
              <Text className="text-xl font-semibold text-foreground">Edit Venue</Text>
              <View className="size-11" />
            </View>

            <Card className="gap-4">
              <View className="gap-2">
                <Text variant="small" className="uppercase tracking-[2px] text-muted-foreground">
                  Live venue page
                </Text>
                <Text className="text-3xl font-extrabold tracking-tight text-foreground">
                  {detail.name}
                </Text>
                <Text variant="muted" className="leading-6">
                  Changes save directly to this venue page so guests always see the latest venue details.
                </Text>
              </View>

              <View className="flex-row gap-3">
                <VenueImageField
                  title="Logo"
                  imageURL={draft.logo_url}
                  onPress={() => handlePickImage('logo_url').catch(() => undefined)}
                  isUploading={uploadingField === 'logo_url'}
                  square
                />
                <VenueImageField
                  title="Cover"
                  imageURL={draft.cover_url}
                  onPress={() => handlePickImage('cover_url').catch(() => undefined)}
                  isUploading={uploadingField === 'cover_url'}
                />
              </View>

              <View className="rounded-[18px] border border-border bg-input/20 px-4 py-3">
                <View className="flex-row items-center gap-3">
                  <View className="size-10 items-center justify-center rounded-full bg-background">
                    <Icon as={categoryConfig.icon} size={18} className="text-foreground" />
                  </View>
                  <View className="flex-1">
                    <Text variant="small" className="font-semibold text-foreground">
                      Current category
                    </Text>
                    <Text variant="small" className="text-muted-foreground">
                      {categoryConfig.label}
                    </Text>
                  </View>
                </View>
              </View>
            </Card>

            <Card className="gap-5">
              <SectionTitle title="Basic Info" />

              <Field label="Venue name">
                <Input
                  value={draft.name}
                  onChangeText={(value) => {
                    venueNameInputTracker.changeText(value);
                    handleFieldChange('name', value);
                  }}
                  onFocus={() => venueNameInputTracker.focus()}
                  onBlur={() => venueNameInputTracker.blur(draft.name)}
                  placeholder="Venue name"
                  autoCapitalize="words"
                />
              </Field>

              <Field label="Description">
                <View className="gap-2">
                  <Textarea
                    value={draft.description}
                    onChangeText={(value) => {
                      const nextValue = value.slice(0, 300);
                      venueDescriptionInputTracker.changeText(nextValue);
                      handleFieldChange('description', nextValue);
                    }}
                    onFocus={() => venueDescriptionInputTracker.focus()}
                    onBlur={() => venueDescriptionInputTracker.blur(draft.description)}
                    placeholder="Tell people what your venue is like."
                    className="min-h-[132px]"
                  />
                  <Text
                    variant="small"
                    className={cn(
                      'text-right text-muted-foreground',
                      draft.description.length > 280 && 'text-primary'
                    )}>
                    {draft.description.length}/300
                  </Text>
                </View>
              </Field>

              <Field label="Category">
                <View className="gap-3">
                  <Pressable
                    onPress={() => setIsCategoryPickerVisible((current) => !current)}
                    className="rounded-md border border-input bg-background p-3 shadow-sm shadow-black/5">
                    <Text className="text-foreground">{findCategoryOption(draft.category)?.label ?? 'Choose a category'}</Text>
                  </Pressable>
                  {isCategoryPickerVisible ? (
                    <View className="gap-2 rounded-[18px] border border-border bg-input/20 p-2">
                      {VENUE_OWNER_CATEGORIES.map((option) => {
                        const selected = draft.category === option.value;
                        return (
                          <Pressable
                            key={option.value}
                            onPress={() => {
                              handleFieldChange('category', option.value as VenueCategory);
                              setIsCategoryPickerVisible(false);
                            }}
                            className={cn(
                              'flex-row items-center justify-between rounded-[14px] p-3',
                              selected ? 'bg-primary/12' : 'bg-transparent'
                            )}>
                            <Text className={selected ? 'font-semibold text-foreground' : 'text-foreground'}>
                              {option.label}
                            </Text>
                            {selected ? <Icon as={CheckIcon} size={16} className="text-primary" /> : null}
                          </Pressable>
                        );
                      })}
                    </View>
                  ) : null}
                </View>
              </Field>

              <Field label="Address">
                <Input
                  value={draft.address}
                  onChangeText={(value) => {
                    venueAddressInputTracker.changeText(value);
                    handleFieldChange('address', value);
                  }}
                  onFocus={() => venueAddressInputTracker.focus()}
                  onBlur={() => venueAddressInputTracker.blur(draft.address)}
                  placeholder="Street address"
                  autoCapitalize="words"
                />
              </Field>

              <Field label="Phone number">
                <Input
                  value={draft.phone}
                  onChangeText={(value) => {
                    const nextValue = formatVenuePhone(value);
                    venuePhoneInputTracker.changeText(nextValue);
                    handleFieldChange('phone', nextValue);
                  }}
                  onFocus={() => venuePhoneInputTracker.focus()}
                  onBlur={() => venuePhoneInputTracker.blur(draft.phone)}
                  placeholder="(555) 123-4567"
                  keyboardType="phone-pad"
                />
              </Field>

              <Field label="Website">
                <Input
                  value={draft.website}
                  onChangeText={(value) => {
                    venueWebsiteInputTracker.changeText(value);
                    handleFieldChange('website', value);
                  }}
                  onFocus={() => venueWebsiteInputTracker.focus()}
                  onBlur={() => venueWebsiteInputTracker.blur(draft.website)}
                  placeholder="https://example.com"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </Field>
            </Card>

            <Card className="gap-5">
              <SectionTitle title="Hours" />
              <View className="gap-3">
                {draft.hours.map((hour) => (
                  <VenueHourRow
                    key={hour.day}
                    hour={hour}
                    activePicker={activePicker}
                    onToggleClosed={handleToggleClosed}
                    onOpenPicker={openPicker}
                    onIOSPickerChange={(field, selectedDate) =>
                      handleHourChange(hour.day, { [field]: dateToTimeString(selectedDate) } as Partial<VenueHour>)
                    }
                    onWebTimeChange={(field, value) => handleHourChange(hour.day, { [field]: value } as Partial<VenueHour>)}
                  />
                ))}
              </View>
            </Card>

            <Card className="gap-5">
              <SectionTitle title="Social Links" />

              <Field label="Add social link">
                <View className="gap-3">
                  <Input
                    value={socialInput}
                    onChangeText={(value) => {
                      socialInputTracker.changeText(value);
                      setSocialInput(value);
                    }}
                    onFocus={() => socialInputTracker.focus()}
                    onBlur={() => socialInputTracker.blur(socialInput)}
                    placeholder="@instagram or https://instagram.com/venue"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <View className="gap-3">
                    <View className="flex-row items-start gap-2">
                      {detectedSocialPlatform ? (
                        <>
                          <Icon as={getSocialPlatformIcon(detectedSocialPlatform)} size={16} className="text-muted-foreground" />
                          <Text variant="small" className="flex-1 text-muted-foreground">
                            {getSocialPlatformLabel(detectedSocialPlatform)}
                          </Text>
                        </>
                      ) : (
                        <Text variant="small" className="flex-1 text-muted-foreground">
                          Instagram handles and supported URLs auto-detect here.
                        </Text>
                      )}
                    </View>
                    <Button
                      className="self-start"
                      size="sm"
                      onPress={handleAddSocial}
                      disabled={!socialInput.trim()}>
                      <Text>Add</Text>
                    </Button>
                  </View>
                </View>
              </Field>

              <View className="gap-3">
                {draft.social_links.length > 0 ? (
                  draft.social_links.map((link) => (
                    <View
                      key={`${link.platform}-${link.url}`}
                      className="flex-row items-center gap-3 rounded-[18px] border border-border bg-input/20 px-4 py-3">
                      <View className="size-9 items-center justify-center rounded-full bg-background">
                        <Icon as={getSocialPlatformIcon(link.platform)} size={16} className="text-foreground" />
                      </View>
                      <View className="flex-1 gap-1">
                        <Text variant="small" className="font-semibold text-foreground">
                          {getSocialPlatformLabel(link.platform)}
                        </Text>
                        <Text variant="small" className="text-muted-foreground" numberOfLines={1}>
                          {link.url}
                        </Text>
                      </View>
                      <Pressable onPress={() => handleRemoveSocial(link)}>
                        <Icon as={Trash2} size={16} className="text-muted-foreground" />
                      </Pressable>
                    </View>
                  ))
                ) : (
                  <Text variant="small" className="text-muted-foreground">
                    No social links added yet.
                  </Text>
                )}
              </View>
            </Card>

            <Card className="gap-5">
              <SectionTitle title="Discovery" />

              <Field label="Tags">
                <View className="gap-4">
                  {EVENT_TAG_GROUPS.map((group) => (
                    <View key={group.key} className="gap-2">
                      <Text variant="small" className="font-semibold text-muted-foreground">
                        {group.label}
                      </Text>
                      <View className="flex-row flex-wrap gap-2">
                        {group.tags.map((tag) => {
                          const active = draft.tags.includes(tag.value);
                          return (
                            <Pressable
                              key={tag.value}
                              onPress={() => handleToggleTag(tag.value)}
                              className={cn(
                                'rounded-full border px-4 py-2.5',
                                active ? 'border-primary bg-primary/10' : 'border-border bg-background'
                              )}>
                              <View className="flex-row items-center gap-2">
                                <View
                                  className="size-2.5 rounded-full"
                                  style={{ backgroundColor: getEventTagColors(tag.value)[0] }}
                                />
                                <Text
                                  variant="small"
                                  className={active ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
                                  {tag.label}
                                </Text>
                              </View>
                            </Pressable>
                          );
                        })}
                      </View>
                    </View>
                  ))}
                </View>
              </Field>

              <Field label="Age restriction">
                <ToggleGroup
                  type="single"
                  value={draft.age_restriction}
                  onValueChange={(value) => {
                    if (value) {
                      handleFieldChange('age_restriction', value as EventAgeRestriction);
                    }
                  }}
                  variant="outline"
                  className="w-full">
                  {EVENT_AGE_RESTRICTIONS.map((option, index) => (
                    <ToggleGroupItem
                      key={option.value}
                      value={option.value}
                      isFirst={index === 0}
                      isLast={index === EVENT_AGE_RESTRICTIONS.length - 1}
                      className="flex-1">
                      <Text className="text-center">{option.label}</Text>
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </Field>

              <Field label="Booking / reservation URL">
                <Input
                  value={draft.booking_url}
                  onChangeText={(value) => {
                    venueBookingUrlInputTracker.changeText(value);
                    handleFieldChange('booking_url', value);
                  }}
                  onFocus={() => venueBookingUrlInputTracker.focus()}
                  onBlur={() => venueBookingUrlInputTracker.blur(draft.booking_url)}
                  placeholder="https://bookings.example.com"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </Field>
            </Card>
          </ScrollView>

          <View className="border-t border-border bg-background px-4 pb-5 pt-4">
            {saveError ? (
              <Text variant="small" className="mb-3 text-primary">
                {saveError}
              </Text>
            ) : isDirty ? (
              <Text variant="small" className="mb-3 text-muted-foreground">
                You have unsaved changes.
              </Text>
            ) : (
              <Text variant="small" className="mb-3 text-muted-foreground">
                Changes save directly to this venue profile.
              </Text>
            )}
            <Button onPress={() => handleSave().catch(() => undefined)} disabled={!canSave}>
              <Text>{updateVenue.isPending ? 'Saving...' : 'Save changes'}</Text>
            </Button>
          </View>
        </View>
      </KeyboardAvoidingView>
    </AppScreen>
  );
}

function buildVenueDraft(detail: VenueDetail): PatchVenueRequest {
  return {
    name: detail.name,
    description: detail.description ?? '',
    category: isOwnerCategory(detail.category) ? detail.category : VenueCategoryOther,
    address: detail.address ?? '',
    phone: detail.phone ?? '',
    website: detail.website ?? '',
    booking_url: detail.booking_url ?? '',
    logo_url: detail.logo_url ?? '',
    cover_url: detail.cover_url ?? '',
    age_restriction: detail.age_restriction,
    tags: [...detail.tags],
    social_links: [...detail.social_links],
    hours: buildCompleteHours(detail.hours),
  };
}

function normalizeVenueDraft(draft: PatchVenueRequest): PatchVenueRequest {
  const name = draft.name.trim();
  if (!name) {
    throw new Error('Venue name is required.');
  }

  const address = draft.address.trim();
  if (!address) {
    throw new Error('Address is required.');
  }

  const description = draft.description.trim();
  if (description.length > 300) {
    throw new Error('Description must be 300 characters or less.');
  }

  const category = isOwnerCategory(draft.category) ? draft.category : VenueCategoryOther;
  const website = normalizeOptionalURL(draft.website, 'Website');
  const bookingURL = normalizeOptionalURL(draft.booking_url, 'Booking URL');
  const socialLinks = draft.social_links
    .map((link) => normalizeSocialLink(link))
    .sort(
      (left, right) => left.platform.localeCompare(right.platform) || left.url.localeCompare(right.url)
    );
  // react-doctor-disable-next-line react-doctor/js-tosorted-immutable
  const tags = [
    ...new Set(draft.tags.flatMap((tag) => {
      const trimmed = tag.trim();
      return trimmed ? [trimmed] : [];
    })),
  ].sort();
  const hours = buildCompleteHours(draft.hours).map((hour) => {
    if (hour.closed) {
      return { ...hour, opens_at: null, closes_at: null };
    }

    const opensAt = (hour.opens_at ?? '').trim();
    const closesAt = (hour.closes_at ?? '').trim();
    if (!isValidTime(opensAt) || !isValidTime(closesAt)) {
      throw new Error(`Enter valid opening and closing times for ${getWeekdayLabel(hour.day)}.`);
    }
    if (opensAt >= closesAt) {
      throw new Error(`${getWeekdayLabel(hour.day)} must open before it closes.`);
    }

    return {
      ...hour,
      opens_at: opensAt,
      closes_at: closesAt,
    };
  });

  return {
    ...draft,
    name,
    description,
    category,
    address,
    phone: draft.phone.trim(),
    website,
    booking_url: bookingURL,
    logo_url: draft.logo_url.trim(),
    cover_url: draft.cover_url.trim(),
    tags,
    social_links: socialLinks,
    hours,
  };
}

function buildCompleteHours(hours: VenueHour[]) {
  return VENUE_WEEKDAYS.map((day) => {
    const existing = hours.find((hour) => hour.day === day);
    if (existing) {
      return {
        day,
        closed: existing.closed,
        opens_at: existing.opens_at ?? null,
        closes_at: existing.closes_at ?? null,
      };
    }

    return {
      day,
      closed: true,
      opens_at: null,
      closes_at: null,
    } satisfies VenueHour;
  });
}

function isOwnerCategory(value?: string | null): value is VenueCategory {
  return VENUE_OWNER_CATEGORIES.some((option) => option.value === value);
}

function findCategoryOption(value: VenueCategory) {
  return VENUE_OWNER_CATEGORIES.find((option) => option.value === value);
}

function normalizeOptionalURL(value: string, label: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(normalized);
    if (!parsed.hostname) {
      throw new Error(`${label} is invalid.`);
    }
    parsed.hash = '';
    parsed.hostname = parsed.hostname.toLowerCase();
    return parsed.toString();
  } catch {
    throw new Error(`${label} is invalid.`);
  }
}

function normalizeSocialLinkInput(input: string): VenueSocialLink {
  const trimmed = input.trim();
  if (!trimmed) {
    throw new Error('Enter a social link or handle.');
  }

  if (trimmed.startsWith('@') || !trimmed.includes('/') && !trimmed.includes('.')) {
    const handle = trimmed.replace(/^@+/, '').trim();
    if (!/^[A-Za-z0-9._]+$/.test(handle)) {
      throw new Error('Instagram handles can only include letters, numbers, dots, and underscores.');
    }

    return {
      platform: VenueSocialPlatformInstagram,
      url: `https://instagram.com/${handle}`,
    };
  }

  const normalized = normalizeOptionalURL(trimmed, 'Social link');
  const platform = detectSocialPlatform(normalized);
  if (!platform) {
    throw new Error('Use Instagram, TikTok, Facebook, X, Spotify, YouTube, or Linktree.');
  }

  return {
    platform,
    url: normalizeSocialURL(normalized, platform),
  };
}

function normalizeSocialLink(link: VenueSocialLink): VenueSocialLink {
  const normalized = normalizeOptionalURL(link.url, 'Social link');
  const detected = detectSocialPlatform(normalized);
  if (!detected || detected !== link.platform) {
    throw new Error(`The ${getSocialPlatformLabel(link.platform)} link is invalid.`);
  }

  return {
    platform: link.platform,
    url: normalizeSocialURL(normalized, link.platform),
  };
}

function detectSocialPlatformPreview(input: string) {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  if (trimmed.startsWith('@') || !trimmed.includes('/') && !trimmed.includes('.')) {
    return VenueSocialPlatformInstagram;
  }

  try {
    return detectSocialPlatform(normalizeOptionalURL(trimmed, 'Social link'));
  } catch {
    return null;
  }
}

function detectSocialPlatform(value: string): VenueSocialPlatform | null {
  const hostname = new URL(value).hostname.toLowerCase().replace(/^www\./, '');
  if (hostname === 'instagram.com') {
    return VenueSocialPlatformInstagram;
  }
  if (hostname === 'tiktok.com' || hostname.endsWith('.tiktok.com')) {
    return VenueSocialPlatformTiktok;
  }
  if (hostname === 'facebook.com') {
    return VenueSocialPlatformFacebook;
  }
  if (hostname === 'twitter.com' || hostname === 'x.com') {
    return VenueSocialPlatformX;
  }
  if (hostname === 'spotify.com' || hostname.endsWith('.spotify.com')) {
    return VenueSocialPlatformSpotify;
  }
  if (hostname === 'youtube.com' || hostname === 'youtu.be') {
    return VenueSocialPlatformYoutube;
  }
  if (hostname === 'linktr.ee') {
    return VenueSocialPlatformLinktree;
  }
  return null;
}

function normalizeSocialURL(value: string, platform: VenueSocialPlatform) {
  const parsed = new URL(value);
  parsed.hash = '';
  parsed.hostname = getPlatformHostname(platform, parsed.hostname);
  if (platform === VenueSocialPlatformInstagram) {
    parsed.pathname = normalizeSocialPath(parsed.pathname);
  }
  if (platform === VenueSocialPlatformX || platform === VenueSocialPlatformFacebook || platform === VenueSocialPlatformLinktree) {
    parsed.pathname = normalizeSocialPath(parsed.pathname);
  }
  return parsed.toString();
}

function getPlatformHostname(platform: VenueSocialPlatform, fallbackHost: string) {
  switch (platform) {
    case VenueSocialPlatformInstagram:
      return 'instagram.com';
    case VenueSocialPlatformTiktok:
      return 'tiktok.com';
    case VenueSocialPlatformFacebook:
      return 'facebook.com';
    case VenueSocialPlatformX:
      return 'x.com';
    case VenueSocialPlatformSpotify:
      return fallbackHost.toLowerCase().replace(/^www\./, '') === 'open.spotify.com'
        ? 'open.spotify.com'
        : 'spotify.com';
    case VenueSocialPlatformYoutube:
      return fallbackHost.toLowerCase().replace(/^www\./, '') === 'youtu.be' ? 'youtu.be' : 'youtube.com';
    case VenueSocialPlatformLinktree:
      return 'linktr.ee';
    default:
      return fallbackHost.toLowerCase();
  }
}

function normalizeSocialPath(pathname: string) {
  const trimmed = pathname.replace(/^\/+|\/+$/g, '');
  return trimmed ? `/${trimmed}` : '/';
}

function getSocialPlatformIcon(platform: VenueSocialPlatform) {
  switch (platform) {
    case VenueSocialPlatformInstagram:
      return Instagram;
    case VenueSocialPlatformTiktok:
      return Music2;
    case VenueSocialPlatformFacebook:
      return Facebook;
    case VenueSocialPlatformX:
      return AtSign;
    case VenueSocialPlatformSpotify:
      return Disc3;
    case VenueSocialPlatformYoutube:
      return Youtube;
    case VenueSocialPlatformLinktree:
      return TreePalm;
    default:
      return Link;
  }
}

function getSocialPlatformLabel(platform: VenueSocialPlatform) {
  switch (platform) {
    case VenueSocialPlatformInstagram:
      return 'Instagram';
    case VenueSocialPlatformTiktok:
      return 'TikTok';
    case VenueSocialPlatformFacebook:
      return 'Facebook';
    case VenueSocialPlatformX:
      return 'X / Twitter';
    case VenueSocialPlatformSpotify:
      return 'Spotify';
    case VenueSocialPlatformYoutube:
      return 'YouTube';
    case VenueSocialPlatformLinktree:
      return 'Linktree';
    default:
      return 'Link';
  }
}

function formatVenuePhone(value: string) {
  const digits = value.replace(/\D+/g, '').slice(0, 10);
  if (digits.length < 4) {
    return digits;
  }
  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidTime(value: string) {
  return /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value);
}

function getExistingHour(hours: VenueHour[] | undefined | null, day: VenueWeekday) {
  return hours?.find((hour) => hour.day === day) ?? null;
}

function getWeekdayLabel(day: VenueWeekday) {
  return day.charAt(0).toUpperCase() + day.slice(1);
}

function timeStringToDate(value: string) {
  const [hoursRaw, minutesRaw] = value.split(':');
  const date = new Date();
  date.setHours(Number(hoursRaw) || 0, Number(minutesRaw) || 0, 0, 0);
  return date;
}

function dateToTimeString(value: Date) {
  return `${value.getHours().toString().padStart(2, '0')}:${value.getMinutes().toString().padStart(2, '0')}`;
}

function formatTimeLabel(value?: string | null) {
  if (!value || !isValidTime(value)) {
    return 'Select time';
  }

  const [hoursRaw, minutesRaw] = value.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const period = hours >= 12 ? 'PM' : 'AM';
  const normalizedHours = hours % 12 || 12;
  return `${normalizedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

async function uploadVenueImage(
  request: <T>(path: string, init?: RequestInit) => Promise<T>,
  asset: ImagePicker.ImagePickerAsset,
  field: 'logo_url' | 'cover_url'
) {
  const fileName = asset.fileName ?? `${field}-${Date.now()}.jpg`;
  const contentType = asset.mimeType ?? 'image/jpeg';
  const body = await assetToUploadBody(asset);
  const signPayload: SignUploadRequest = {
    file_name: fileName,
    content_type: contentType,
    purpose: UploadPurposeVenueImage,
    file_size_bytes: body.size,
  };
  const upload = await request<PresignedUpload>('/uploads/sign', {
    method: 'POST',
    body: JSON.stringify(signPayload),
  });
  const response = await fetch(upload.url, {
    method: upload.method,
    headers: upload.headers,
    body,
  });
  if (!response.ok) {
    throw new Error('Venue image upload failed.');
  }

  return upload.public_url;
}

async function assetToUploadBody(asset: ImagePicker.ImagePickerAsset) {
  if (asset.file) {
    return asset.file;
  }

  const response = await fetch(asset.uri);
  if (!response.ok) {
    throw new Error('Unable to read the selected image.');
  }
  return await response.blob();
}

function confirmDiscardChanges(onDiscard: () => void) {
  if (Platform.OS === 'web' && typeof globalThis.confirm === 'function') {
    if (globalThis.confirm('Discard your unsaved venue changes?')) {
      onDiscard();
    }
    return;
  }

  Alert.alert('Discard changes?', 'You have unsaved venue edits.', [
    { text: 'Keep editing', style: 'cancel' },
    { text: 'Discard', style: 'destructive', onPress: onDiscard },
  ]);
}

function SectionTitle({ title }: { title: string }) {
  return <Text className="text-2xl font-semibold text-foreground">{title}</Text>;
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <View className="gap-2">
      <Text variant="small" className="font-semibold uppercase tracking-[1.4px] text-muted-foreground">
        {label}
      </Text>
      {children}
    </View>
  );
}

function VenueImageField({
  title,
  imageURL,
  onPress,
  isUploading,
  square = false,
}: {
  title: string;
  imageURL: string;
  onPress: () => void;
  isUploading: boolean;
  square?: boolean;
}) {
  return (
    <Pressable onPress={onPress} className="flex-1 gap-2">
      <Text variant="small" className="font-semibold uppercase tracking-[1.4px] text-muted-foreground">
        {title}
      </Text>
      <View
        className={cn(
          'items-center justify-center overflow-hidden rounded-[22px] border border-border bg-input/20',
          square ? 'aspect-square' : 'aspect-[16/10]'
        )}>
        {imageURL ? (
          <RNImage source={{ uri: imageURL }} className="h-full w-full" resizeMode="cover" />
        ) : (
          <View className="items-center gap-2 px-4">
            <Icon as={Image} size={20} className="text-muted-foreground" />
            <Text variant="small" className="text-center text-muted-foreground">
              Add {title.toLowerCase()}
            </Text>
          </View>
        )}
        <View className="absolute bottom-3 right-3 rounded-full bg-background px-3 py-2">
          <View className="flex-row items-center gap-2">
            <Icon as={Camera} size={14} className="text-foreground" />
            <Text variant="small" className="font-semibold text-foreground">
              {isUploading ? 'Uploading...' : imageURL ? 'Change' : 'Upload'}
            </Text>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

function VenueHourRow({
  hour,
  activePicker,
  onToggleClosed,
  onOpenPicker,
  onIOSPickerChange,
  onWebTimeChange,
}: {
  hour: VenueHour;
  activePicker: ActivePicker;
  onToggleClosed: (day: VenueWeekday, closed: boolean) => void;
  onOpenPicker: (day: VenueWeekday, field: PickerField) => void;
  onIOSPickerChange: (field: PickerField, selectedDate: Date) => void;
  onWebTimeChange: (field: PickerField, value: string) => void;
}) {
  return (
    <View className="rounded-[22px] border border-border bg-input/20 p-4">
      <View className="flex-row items-center justify-between gap-3">
        <View className="gap-1">
          <Text className="text-base font-semibold text-foreground">{getWeekdayLabel(hour.day)}</Text>
          <Text variant="small" className="text-muted-foreground">
            {hour.closed ? 'Closed' : 'Open'}
          </Text>
        </View>
        <View className="flex-row items-center gap-3">
          <Text variant="small" className="text-muted-foreground">
            Closed
          </Text>
          <Switch checked={hour.closed} onCheckedChange={(value) => onToggleClosed(hour.day, value)} />
        </View>
      </View>

      {!hour.closed ? (
        <View className="mt-4 gap-3">
          {Platform.OS === 'web' ? (
            <View className="flex-row gap-3">
              <Input
                value={hour.opens_at ?? ''}
                onChangeText={(value) => onWebTimeChange('opens_at', value)}
                placeholder="17:00"
                autoCapitalize="none"
              />
              <Input
                value={hour.closes_at ?? ''}
                onChangeText={(value) => onWebTimeChange('closes_at', value)}
                placeholder="23:00"
                autoCapitalize="none"
              />
            </View>
          ) : (
            <View className="flex-row gap-3">
              <Pressable
                onPress={() => onOpenPicker(hour.day, 'opens_at')}
                className="flex-1 rounded-[18px] border border-border bg-background px-4 py-3">
                <Text variant="small" className="font-semibold text-foreground">
                  Opens
                </Text>
                <Text className="mt-1 text-foreground">{formatTimeLabel(hour.opens_at)}</Text>
              </Pressable>
              <Pressable
                onPress={() => onOpenPicker(hour.day, 'closes_at')}
                className="flex-1 rounded-[18px] border border-border bg-background px-4 py-3">
                <Text variant="small" className="font-semibold text-foreground">
                  Closes
                </Text>
                <Text className="mt-1 text-foreground">{formatTimeLabel(hour.closes_at)}</Text>
              </Pressable>
            </View>
          )}

          {Platform.OS === 'ios' && activePicker?.day === hour.day ? (
            <View className="overflow-hidden rounded-[18px] border border-border bg-background px-3 py-2">
              <DateTimePicker
                value={timeStringToDate(
                  activePicker.field === 'opens_at' ? hour.opens_at ?? '17:00' : hour.closes_at ?? '23:00'
                )}
                mode="time"
                display="spinner"
                onChange={(_event: DateTimePickerEvent, selectedDate?: Date) => {
                  if (!selectedDate) {
                    return;
                  }
                  onIOSPickerChange(activePicker.field, selectedDate);
                }}
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}
